/**
 * linearSemanticApi — thin client for the integrations engine's Linear reads
 * (engines/integrations-engine/src/routes/linear.ts). Beside miroExcursionApi /
 * notionSemanticApi; keeps integrationsApi.ts untouched for the lint gate.
 *
 *   • import-semantic — a team's or a project's issues with their relations,
 *     parent chain, label/state color and fields (priority, state, labels…),
 *     for the Linear import.
 *   • projects / issues/search / issue — what linking one item to one issue
 *     needs: pick a project, find the issue, re-read it on sync.
 */

import { INTEGRATIONS_ENGINE_URL } from '../engineUrls';
import { getSafeSupabaseClient } from '../supabaseSync';

/** A Linear IssueRelation: the relation type + the issues it points at. */
export interface LinearRelationRead {
  /** 'blocks' | 'related' | 'duplicate' — the cue that resolves to a canonical type. */
  type: string;
  targetExternalIds: string[];
}

export interface LinearIssueRead {
  externalId: string;
  externalUrl?: string;
  title: string;
  description?: string;
  /** First label color, else the workflow-state color. */
  categoryColor?: string;
  /** The parent issue's external id when this is a sub-issue. */
  parentExternalId?: string;
  relations: LinearRelationRead[];
  /** The issue's fields as the Linear field mappings name them (priority, state, labels, identifier…). */
  attributes?: Record<string, unknown>;
}

export interface LinearTeamRead {
  team: { id: string; name: string; url?: string };
  issues: LinearIssueRead[];
}

/** One issue, as the link panel and the sync read it. */
export interface LinearIssueSummary {
  id: string;
  /** The key a person recognizes, e.g. PRI-12. */
  identifier: string;
  title: string;
  description?: string | null;
  /** 0 = No priority, 1 = Urgent, 2 = High, 3 = Medium, 4 = Low. */
  priority: number;
  priorityLabel: string;
  url: string;
  state?: { id: string; name: string; type: string; color?: string } | null;
  labels: string[];
  team?: { id: string; name: string; key: string } | null;
  project?: { id: string; name: string } | null;
  estimate?: number | null;
  assignee?: { id: string; name: string; email: string } | null;
  dueDate?: string | null;
  updatedAt: string;
}

export interface LinearProjectSummary {
  id: string;
  name: string;
  state?: string;
  team?: { id: string; name: string };
}

export interface LinearTeamSummary {
  id: string;
  name: string;
  key?: string;
}

async function engineRequest<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  // The signed-in person, for the routes that check who is calling (the initiative routes do).
  const { data } = (await getSafeSupabaseClient()?.auth.getSession()) ?? { data: { session: null } };
  const token = data.session?.access_token;
  const response = await fetch(`${INTEGRATIONS_ENGINE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { success?: boolean; error?: string; details?: string };
  if (!response.ok || payload.success === false) {
    throw new Error(payload.error ?? payload.details ?? `Integrations engine error ${response.status}`);
  }
  return payload;
}

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  return q.toString();
}

/**
 * Read a Linear team's — or one project's — issues as a semantic dialect
 * surface: issues with their relations, parent chain, category color and
 * fields. At least one of teamId / projectId is required.
 */
export async function linearImportSemantic(
  workspaceId: string,
  scope: { teamId?: string; projectId?: string },
): Promise<LinearTeamRead> {
  return engineRequest<LinearTeamRead>('POST', '/linear/import-semantic', { workspaceId, ...scope });
}

/** Connection status — Linear OAuth/connect lives in Settings → Connectors. */
export async function linearStatus(workspaceId: string): Promise<{ connected: boolean }> {
  const r = await engineRequest<{ success: boolean; connected: boolean }>(
    'GET',
    `/linear/status?${query({ workspaceId })}`,
  );
  return { connected: Boolean(r.connected) };
}

/** Teams available to import from. */
export async function linearTeams(workspaceId: string): Promise<LinearTeamSummary[]> {
  const r = await engineRequest<{ success: boolean; teams: LinearTeamSummary[] }>(
    'GET',
    `/linear/teams?${query({ workspaceId })}`,
  );
  return r.teams ?? [];
}

/** Projects in the connected Linear workspace (optionally one team's). */
export async function linearProjects(workspaceId: string, teamId?: string): Promise<LinearProjectSummary[]> {
  const r = await engineRequest<{ success: boolean; projects: LinearProjectSummary[] }>(
    'GET',
    `/linear/projects?${query({ workspaceId, teamId })}`,
  );
  return r.projects ?? [];
}

/**
 * Issues to link an item to: in one project and/or team, matching a title
 * fragment or an exact key (PRI-12). Most recently updated first.
 */
export async function linearSearchIssues(
  workspaceId: string,
  scope: { projectId?: string; teamId?: string; query?: string; first?: number },
): Promise<LinearIssueSummary[]> {
  const r = await engineRequest<{ success: boolean; issues: LinearIssueSummary[] }>(
    'GET',
    `/linear/issues/search?${query({ workspaceId, projectId: scope.projectId, teamId: scope.teamId, q: scope.query, first: scope.first })}`,
  );
  return r.issues ?? [];
}

/** One issue, by its id or its key (PRI-12). */
export async function linearIssue(workspaceId: string, issueId: string): Promise<LinearIssueSummary> {
  const r = await engineRequest<{ success: boolean; issue: LinearIssueSummary }>(
    'GET',
    `/linear/issue?${query({ workspaceId, issueId })}`,
  );
  if (!r.issue) throw new Error('Linear returned no issue');
  return r.issue;
}

/** A Linear initiative: what an Objective can be linked to there. */
export interface LinearInitiativeSummary {
  id: string;
  name: string;
  description: string | null;
  status: string;
  targetDate: string | null;
  url: string;
  updatedAt: string;
}

/** Initiatives whose name holds the words (all of them without), most recently updated first. */
export async function linearSearchInitiatives(workspaceId: string, q?: string): Promise<LinearInitiativeSummary[]> {
  const r = await engineRequest<{ success: boolean; initiatives: LinearInitiativeSummary[] }>(
    'GET',
    `/linear/initiatives/search?${query({ workspaceId, q })}`,
  );
  return r.initiatives ?? [];
}

/** One initiative, by its id. */
export async function linearInitiative(workspaceId: string, initiativeId: string): Promise<LinearInitiativeSummary> {
  const r = await engineRequest<{ success: boolean; initiative: LinearInitiativeSummary }>(
    'GET',
    `/linear/initiative?${query({ workspaceId, initiativeId })}`,
  );
  if (!r.initiative) throw new Error('Linear returned no initiative');
  return r.initiative;
}
